
import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { disposeTooltip } from "@cervval.js/client-html5-core/src/components/disposable";
import { Event, EventHandler, NoBubblesEventInit } from "@cervval.js/client-html5-core/src/components/event.decorator";
import { Icon, buildIcon } from "@cervval.js/client-html5-core/src/components/models/icon";
import { TypedHtmlElements } from "@cervval.js/client-html5-core/src/components/typedHtmlElements.decorator";
import { TypedWebComponentOptions, WebComponentOptions } from "@cervval.js/client-html5-core/src/components/typedWebComponentOptions.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { toggleClass } from "@cervval.js/client-html5-core/src/tools/css.tools";
import { PADDING } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { CssClassHelper } from "@cervval.js/client-html5-core/src/tools/cssClassHelper";
import { createHtmlElement, empty, setHTMLElementContent } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { ValueOrFunction, getVOFValue } from "@cervval.js/common/src/tools/object.tools";
import fastdom from "fastdom/fastdom";
import { Tooltip } from "../../bootstrap";
import { CjsIcon } from "../icon/icon";

export enum ButtonSize {
    SMALL = "small",
    NORMAL = "normal",
    LARGE = "large"
}

/** 
 * Bootstrap button styles available
 * @see https://getbootstrap.com/docs/5.0/components/buttons/
 */
export enum ButtonStyle {
    NONE = "none",
    PRIMARY = "primary",
    SECONDARY = "secondary",
    SUCCESS = "success",
    DANGER = "danger",
    WARNING = "warning",
    INFO = "info",
    LIGHT = "light",
    DARK = "dark",
    LINK = "link"
}

export type ButtonIconMode = "after" | "before" | "only";

export interface ButtonOptions {

    /** Label of button */
    label?: string;

    /** Ref attribute of cjs-button component */
    refAttribute?: string;

    /**
     * Options of tooltip
     */
    tooltip?: Partial<Tooltip.Options>;

    /** 
     * Text color of button 
     * Accepts rgb and hexadecimal format
     * ! Set cssStyle option to "NONE" is advised when using this option
     */
    textColor?: string;

    /** 
     * Background color of button 
     * Accepts rgb and hexadecimal format
     * ! Set cssStyle option to "NONE" is advised when using this option
     */
    backgroundColor?: string;

    /** 
     * Disable button, user can't click or interact with button anymore
     * @default false 
     */
    disabled?: ValueOrFunction<boolean>;

    /** CSS classes for button */
    cssClasses?: string[];

    /** Style of button (primary, danger, info, etc.) */
    cssStyle?: ButtonStyle;

    /**
     * Size of button, accepts 3 types of values 
     * 
     * ButtonSize:
     * SMALL: btn-sm class from bootstrap
     * NORMAL: default size
     * LARGE: btn-lg class from bootstrap
     * 
     * Custom number scale multiplier:
     * number: custom multiplier size from original size
     * 
     * Custom string css dimension:
     * string: css dimension value (px, em, vh, vw, %, etc.)
     */
    size?: ButtonSize | number | string;

    /**
     * Default size of button (base dimension to apply sizing factors, in px)
     */
    defaultSize?: number;

    /** Icon of button */
    icon?: Icon | CjsIcon;

    /** Set label position, after or before button */
    iconMode?: ButtonIconMode;

    /** Outline button, default: false */
    outline?: boolean;

    /** Sharp button, remove border-radius on button */
    sharp?: boolean;

    /** Raised button, add shadow and pressed effect */
    raised?: boolean;

    /** rounded button */
    rounded?: boolean;

    /** Fab button (eq: round button), set border-radius to 50% */
    fab?: boolean;

    /** If true, Disable box shadow of button */
    disableBoxShadow?: boolean;

    /** If true tooltip is only append on 'this' and not in button, don't change tooltip reference when button is disabled/enabled */
    disableTooltipIfButtonIsDisabled?: boolean;
}

export const defaultButtonOptions: ButtonOptions = {

    /** Label of button */
    label: undefined,

    /** Style of button (primary, danger, info, etc.) */
    cssStyle: ButtonStyle.PRIMARY,

    /**
     * Size of button, accepts 3 types of values 
     * 
     * ButtonSize:
     * SMALL: btn-sm class from bootstrap
     * NORMAL: default size
     * LARGE: btn-lg class from bootstrap
     * 
     * Custom number scale multiplier:
     * number: custom multiplier size from original size
     * 
     * Custom string css dimension:
     * string: css dimension value (px, em, vh, vw, %, etc.)
     */
    size: ButtonSize.NORMAL,

    /**
     * Default size of button (base dimension to apply sizing factors, in px)
     */
    defaultSize: 20,

    /** Set label position, after or before button */
    iconMode: "after"

};

// Css classes
export const BTN_CLASS = "btn";
export const BTN_OUTLINE = "outline";
export const BTN_CLASS_SEPARATOR = "-";
export const BTN_SHARP = "sharp";
const BTN_FAB = "fab";
export const BTN_ROUNDED = "rounded-btn";
export const BTN_RAISED = "raised";
export const BTN_BOX_SHADOW_DISABLED = "box-shadow-disabled";

@CustomElement({
    name: "cjs-button",
    style: require("./button.scss"),
    template: require("./button.html")
})
export class CjsButton<I18nType = unknown> extends WebComponent<I18nType> {

    /** 
     * Component HTML elements
     */
    @TypedHtmlElements()
    public readonly components!: {
        button: HTMLButtonElement,
        icon: HTMLElement
    };

    /** 
     * Stores the label that was in the component before init was called (coming from html file)
     * (can not be a fragment because we must keep the children inside between refreshes)
     */
    protected _htmlParsedLabel: HTMLDivElement = createHtmlElement("div");

    @TypedWebComponentOptions(defaultButtonOptions)
    public readonly options!: WebComponentOptions<ButtonOptions>;

    /** On click event is triggered */
    @Event(NoBubblesEventInit)
    public clickEvent!: EventHandler;

    /** Shortcut for button component of ButtonCjs */
    public get button(): HTMLButtonElement {
        return this.components.button;
    }

    /** get the value of disabled property of options  */
    public get disabled(): boolean {
        return Boolean(getVOFValue(this.options.disabled));
    }

    /** Tooltip of button */
    public tooltip?: Tooltip;

    /** Store added css classes from options, use to clean button classes when refreshing component display */
    private _cssClassHelper = new CssClassHelper(this.button);

    /**
     * Flag used to know if button was removed from DOM
     * ! Doesn't mean than button is not currently connected to DOM !
     */
    protected _wasRemovedFromDOM = false;

    constructor(options?: Partial<ButtonOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this.options.setWithoutRefresh(options);
    }

    /**
     * Redirect parsed content inside button
     */
    public override getParsedInnerHtmlTarget(): Node | undefined {
        return this._htmlParsedLabel;
    }

    /** 
     * When component is initialized 
     */
    public override init(): void {

        // Init listeners
        this._initListeners();
    }

    /**
     * Init component listeners
     */
    protected _initListeners(): void {

        // Add event click listener
        this.button.addEventListener("click", () => this.clickEvent.emit());
    }

    /**
     * Component refresh 
     */
    public override refresh(reason?: string): void {

        // Set button label
        this._refreshLabel();

        // Set button aria-label
        this._refreshAriaLabel();

        if (this.options.refAttribute) {
            this.setAttribute("ref", this.options.refAttribute);
        }

        // Refresh disabled of button
        this._refreshDisabled();

        // Refresh icon
        this._refreshIcon();

        // Refresh button display
        this.refreshDisplay();

        // Refresh button size
        this._refreshSize();
    }

    /** When button is added to DOM */
    public connectedCallback(): void {

        // Set tooltip when button is added to DOM, only if button was already removed from DOM once
        if (this.options.tooltip && !this.tooltip && this._wasRemovedFromDOM) {
            // if tooltip in option, add it
            this._refreshTooltip();
        }
    }

    /** When button is removed from DOM */
    public disconnectedCallback(): void {
        // Remove tooltip when button is removed from the DOM
        disposeTooltip(this);

        // Set to true, used in connectedCallback
        this._wasRemovedFromDOM = true;
    }

    /** Refresh display of button */
    public refreshDisplay(): void {

        // Clean added css classes
        this._cssClassHelper.clear();

        // Style class
        let styleClass = BTN_CLASS;
        let shouldAddClass = false;

        // Outline
        if (this.options.outline) {
            styleClass += BTN_CLASS_SEPARATOR + BTN_OUTLINE;
            shouldAddClass = true;
        }

        if (this.options.cssStyle !== ButtonStyle.NONE) {
            styleClass += BTN_CLASS_SEPARATOR + this.options.cssStyle;
            shouldAddClass = true;
        }

        if (shouldAddClass) {
            this._cssClassHelper.add(styleClass);
        }

        // Add custom classes
        this._cssClassHelper.add(...this.options.cssClasses ?? []);

        // Sharp
        if (this.options.sharp) {
            this.button.classList.add(BTN_SHARP);
        } else {
            this.button.classList.remove(BTN_SHARP);
        }

        // Fab
        if (this.options.fab) {
            fastdom.measure(() => {// measure then mutate dom
                const height = this.button.getBoundingClientRect().height;
                fastdom.mutate(() => {
                    this.button.classList.add(BTN_FAB);
                    if (height !== 0) {// Resize only if button is added to DOM
                        this.button.style.width = height + "px";
                        this.button.style.height = height + "px";
                    }
                });
            });
        } else {
            this.button.classList.remove(BTN_FAB);
        }

        // Set rounded
        if (this.options.rounded) {
            this.button.classList.add(BTN_ROUNDED);
        } else {
            this.button.classList.remove(BTN_ROUNDED);
        }

        // Set raised
        if (this.options.raised) {
            this.button.classList.add(BTN_RAISED);
        } else {
            this.button.classList.remove(BTN_RAISED);
        }

        // Set tooltip
        this._refreshTooltip();

        // Set text color
        if (this.options.textColor) {
            this.button.style.color = this.options.textColor;
        }

        // Set background color
        if (this.options.backgroundColor) {
            this.button.style.backgroundColor = this.options.backgroundColor;
        }

        // Toggle box-shadow effect of button
        toggleClass(this.button, this.options.disableBoxShadow, BTN_BOX_SHADOW_DISABLED);

        // Update disabled attribute
        this._refreshDisabled();
    }

    /** dispose or install tooltip */
    protected _refreshTooltip(): void {

        if (this.tooltip) {
            disposeTooltip(this);
        }
        if (this.options.tooltip) {
            /**
             * ternary is used to bypass a bootstrap limitation : "Tooltips for .disabled or disabled elements must be triggered on a wrapper element."
             * https://getbootstrap.com/docs/4.0/components/tooltips/
             */
            this.tooltip = new Tooltip(this.disabled || this.options.disableTooltipIfButtonIsDisabled ? this : this.components.button, this.options.tooltip);
        }
    }

    /**
     * Set label of button
     */
    protected _refreshLabel(): void {
        if (this.options.label != null) {
            this.button.innerText = this.options.label;
        } else {
            // if no label is provided, we keep the label parsed from html
            setHTMLElementContent(this.components.button, this._htmlParsedLabel);
        }
    }

    /**
     * Set aria-label of button
     */
    protected _refreshAriaLabel(): void {
        this.button.ariaLabel = this.options.label ?? "";
    }

    /**
     * Update disabled state
     */
    protected _refreshDisabled(): void {
        this.button.disabled = this.disabled;
    }

    /**
     * refresh button icon
     * Set icon layout
     */
    protected _refreshIcon(): void {
        let iconComponent: HTMLElement = this.components.icon;
        const iconOption = this.options.icon;

        if (iconOption) {
            if (iconOption instanceof CjsIcon) {
                iconComponent = iconOption;
            } else {
                iconComponent.style.visibility = "visible";
                iconComponent.className = buildIcon(iconOption).className;
                if (iconOption.htmlString != null) {
                    iconComponent.innerHTML = iconOption.htmlString;
                }
            }

            // Set label position (refresh don't need to be handled, accept than label position is never updated)
            if (this.options.iconMode === "before") {
                this.button.prepend(iconComponent);
                toggleClass(iconComponent, Boolean(this.options.label), PADDING.PE_2);
                iconComponent.classList.remove(PADDING.PS_2);

                // After icon
            } else if (this.options.iconMode === "after") {
                this.button.append(iconComponent);
                toggleClass(iconComponent, Boolean(this.options.label), PADDING.PS_2);
                iconComponent.classList.remove(PADDING.PE_2);

                // Icon only: Remove text if icon only is set
            } else if (this.options.iconMode === "only") {
                // Remove previous classes from other iconMode
                iconComponent.classList.remove(PADDING.PS_2, PADDING.PE_2);
                empty(this.button);
                this.button.append(iconComponent);
            }
        } else {
            iconComponent.style.visibility = "hidden";
        }
    }

    /** Refresh button size, based on options.size */
    protected _refreshSize(): void {
        const defaultSize = this.options.defaultSize ?? defaultButtonOptions.defaultSize!;

        if (typeof this.options.size === "number") {// Custom size defined (number type)

            // Apply scale factor in options to baseDimension of button
            this.button.style.height = this.options.size * defaultSize + "px";
            this.button.style.width = this.options.size * defaultSize * 2 + "px";// Admit than btn is 2x time larger than taller

            // ButtonSize size defined (check if value exist in enum)
        } else if (Object.values(ButtonSize).includes(this.options.size as ButtonSize)) {


            // Get scale value from enum in options
            switch (this.options.size) {
                case ButtonSize.LARGE:
                    this.button.classList.add("btn-lg");
                    break;
                case ButtonSize.SMALL:
                    this.button.classList.add("btn-sm");
                    break;
                case ButtonSize.NORMAL:
                default:
                    break;
            }

        } else {// Custom size defined (string type)  (ex: 4em / 20px / 1vh / etc.)
            // Set the width
            this.button.style.width = this.options.size ?? ButtonSize.NORMAL;

            if (this.options.fab) {// Set height also if it's a fab btn (round)
                this.button.style.height = this.options.size ?? ButtonSize.NORMAL;
            }
        }
    }
}